/**
 * @file Leave workflow settings owner.
 */

import { appData, tempConfigData } from "../../app/state/state.js";
import {
    NATIVE_CHECKBOX_CLASSES,
    NATIVE_CHECKBOX_ICON_CLASSES,
    NATIVE_CHECKBOX_LABEL_CLASSES,
    NATIVE_CHECKBOX_MARKER_CLASSES,
    NATIVE_INPUT_CLASSES,
    bindNativeFieldBehavior,
} from "../../shared/forms/nativeFieldStyles.js";
import { createTeamOptions } from "../../shared/ui/controls/controlOptions.js";
import { createSelectControl } from "../../shared/ui/controls/selectControl.js";
import {
    destroyRegisteredControlsIn,
} from "../../shared/ui/controls/controlCore.js";
import { openConfirmationModal } from "../../shared/ui/confirmationModal.js";
import { escapeHTML } from "../../shared/utils/contentUtils.js";

const APPROVAL_MODE_OPTIONS = Object.freeze([
    { value: "manager_only", label: "Manager only" },
    { value: "manager_and_two_up", label: "Manager and two-up" },
]);
const DEFAULT_LEAVE_SETTINGS = Object.freeze({
    approvalMode: "manager_only",
    detailTeamId: null,
    defaultTypeId: null,
    escalationTimeoutHours: 72,
});

let updateSaveButtonHandler = () => {};
let pendingLeaveTypeFocusId = "";
let showArchivedLeaveTypes = false;

function attrValue(value = "") {
    return escapeHTML(String(value ?? ""))
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#39;");
}

function ensureLeaveConfig() {
    if (!tempConfigData.leave || typeof tempConfigData.leave !== "object") {
        tempConfigData.leave = {};
    }
    tempConfigData.leave = {
        ...DEFAULT_LEAVE_SETTINGS,
        ...tempConfigData.leave,
    };
    delete tempConfigData.leave.defaultReminderLeadDays;
    if (!Array.isArray(tempConfigData.leave.types)) {
        tempConfigData.leave.types = [];
    }
    return tempConfigData.leave;
}

function normalizeWholeNumber(value, fallback = 0) {
    const numberValue = Number(value);
    if (!Number.isInteger(numberValue)) {return fallback;}
    return numberValue;
}

function getVisibleLeaveTypes(leaveConfig) {
    return [...leaveConfig.types]
        .filter((type) => showArchivedLeaveTypes || type?.isActive !== false)
        .sort((a, b) => {
            const activeDiff = (a?.isActive === false ? 1 : 0) -
                (b?.isActive === false ? 1 : 0);
            if (activeDiff !== 0) {return activeDiff;}
            return (Number(a?.order) || 0) - (Number(b?.order) || 0);
        });
}

function getActiveLeaveTypeOptions(leaveConfig) {
    return getVisibleLeaveTypes({
        ...leaveConfig,
        types: leaveConfig.types.filter((type) => type?.isActive !== false),
    }).map((type) => ({
        value: type.id,
        label: type.name || "Unnamed leave type",
    }));
}

function getMaxLeaveTypeOrder() {
    return Math.max(
        0,
        ...ensureLeaveConfig().types.map((type) => Number(type?.order) || 0),
    );
}

function requestLeaveTypeFocus(id) {
    pendingLeaveTypeFocusId = id;
    requestAnimationFrame(() => {
        const index = ensureLeaveConfig().types.findIndex((type) => type?.id === id);
        pendingLeaveTypeFocusId = "";
        if (index < 0) {return;}
        const input = document.querySelector(
            `[data-leave-type-name][data-index="${index}"]`,
        );
        input?.focus();
        input?.select();
    });
}

function renderLeaveTypeOrderButton({index, direction, disabled}) {
    const icon = direction === "up" ? "expand_less" : "expand_more";
    const label = direction === "up" ? "Move leave type up" :
        "Move leave type down";
    return `
        <button
            type="button"
            class="leave-type-order-btn inline-flex h-7 w-7 items-center justify-center rounded-full text-gray-400 transition-colors hover:bg-gray-50 hover:text-[var(--dfc-brand-primary)] disabled:cursor-not-allowed disabled:opacity-25"
            data-index="${index}"
            data-direction="${direction}"
            title="${label}"
            aria-label="${label}"
            ${disabled ? "disabled" : ""}
        >
            <span class="material-symbols-outlined pointer-events-none text-[18px]">${icon}</span>
        </button>
    `;
}

function renderLeaveTypeAction(type, index) {
    const isActive = type.isActive !== false;
    const hasBeenUsed = type.hasBeenUsed === true;

    if (!hasBeenUsed) {
        return `
            <button
                type="button"
                class="delete-leave-type-btn settings-config-delete"
                data-index="${index}"
                title="Delete leave type"
                aria-label="Delete leave type"
            >
                <span class="material-symbols-outlined text-lg">delete</span>
            </button>
        `;
    }

    return `
        <button
            type="button"
            class="${isActive ? "archive-leave-type-btn" : "restore-leave-type-btn"} settings-config-archive ${isActive ? "" : "settings-config-restore"}"
            data-index="${index}"
            title="${isActive ? "Archive leave type" : "Restore leave type"}"
            aria-label="${isActive ? "Archive leave type" : "Restore leave type"}"
        >
            <span class="material-symbols-outlined text-lg">${isActive ? "archive" : "unarchive"}</span>
        </button>
    `;
}

function renderShowArchivedCheckbox() {
    return `
        <label class="${NATIVE_CHECKBOX_LABEL_CLASSES}" data-native-checkbox-label>
            <input
                type="checkbox"
                class="${NATIVE_CHECKBOX_CLASSES}"
                data-leave-show-archived
                ${showArchivedLeaveTypes ? "checked" : ""}
            >
            <span class="${NATIVE_CHECKBOX_MARKER_CLASSES}" data-native-checkbox-marker>
                <span class="${NATIVE_CHECKBOX_ICON_CLASSES} opacity-0" data-native-checkbox-icon>check</span>
            </span>
            <span class="text-sm text-gray-600">Show archived</span>
        </label>
    `;
}

function renderLeaveTypeRow(type, index, sortedTypes) {
    const sameStateTypes = sortedTypes.filter((entry) =>
        (entry?.isActive !== false) === (type?.isActive !== false));
    const stateIndex = sameStateTypes.findIndex((entry) => entry?.id === type.id);
    const isActive = type.isActive !== false;
    const inactiveAttr = isActive ? "" : "disabled";
    return `
        <div class="leave-type-row ${isActive ? "" : "opacity-75"}">
            <div class="settings-config-order-controls leave-type-order" aria-label="Leave type order controls">
                ${renderLeaveTypeOrderButton({
        index,
        direction: "up",
        disabled: !isActive || stateIndex <= 0,
    })}
                ${renderLeaveTypeOrderButton({
        index,
        direction: "down",
        disabled: !isActive || stateIndex >= sameStateTypes.length - 1,
    })}
            </div>
            <div class="settings-type-row-main">
                <input
                    type="text"
                    class="settings-config-name leave-type-name"
                    value="${attrValue(type.name || "")}"
                    placeholder="Leave type name"
                    data-leave-type-name
                    data-index="${index}"
                    ${inactiveAttr}
                >
            </div>
            <div class="leave-type-action">
                ${renderLeaveTypeAction(type, index)}
            </div>
        </div>
    `;
}

function renderLeaveTypesEditor(leaveConfig) {
    const sortedTypes = getVisibleLeaveTypes(leaveConfig);
    return `
        <section class="bg-white rounded-lg shadow-md p-6 space-y-4">
            <div class="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                <div>
                    <h4 class="text-sm font-semibold text-gray-700">Leave types</h4>
                    <p class="mt-1 text-sm text-gray-500">
                        Active leave types are available when users request leave.
                    </p>
                </div>
                ${renderShowArchivedCheckbox()}
            </div>
            <div class="max-w-md space-y-1">
                <label class="block text-sm font-medium text-gray-700">Default leave type</label>
                <div data-leave-default-type-control></div>
            </div>
            <div class="leave-type-list" data-leave-types-list>
                ${sortedTypes.map((type) => {
        const index = leaveConfig.types.findIndex((entry) => entry?.id === type.id);
        return index >= 0 ? renderLeaveTypeRow(type, index, sortedTypes) : "";
    }).join("") || `
                    <p class="rounded-md border border-dashed border-gray-200 bg-gray-50 px-4 py-6 text-center text-sm text-gray-500">
                        No leave types configured.
                    </p>
                `}
            </div>
            <button type="button" class="add-leave-type-btn settings-add-row-btn">
                + Add Leave Type
            </button>
        </section>
    `;
}

function mountLeaveSelectControls(container) {
    const leaveConfig = ensureLeaveConfig();
    const approvalRoot = container.querySelector("[data-leave-approval-mode-control]");
    if (approvalRoot) {
        createSelectControl({
            root: approvalRoot,
            options: APPROVAL_MODE_OPTIONS,
            value: leaveConfig.approvalMode || "manager_only",
            accessibleName: "Leave approval mode",
            placeholder: "Select approval mode",
            portalMenu: true,
            onChange: (value) => {
                leaveConfig.approvalMode = value || "manager_only";
                updateSaveButtonHandler(true);
            },
        });
    }

    const detailTeamRoot = container.querySelector("[data-leave-detail-team-control]");
    if (detailTeamRoot) {
        const selectedValue = leaveConfig.detailTeamId || "";
        createSelectControl({
            root: detailTeamRoot,
            options: createTeamOptions(appData.teams || [], {
                includeHidden: true,
                selectedValues: selectedValue,
            }),
            value: selectedValue,
            accessibleName: "Leave management team",
            placeholder: "No shared team",
            searchPlaceholder: "Search teams",
            clearable: true,
            searchable: true,
            searchThreshold: 7,
            portalMenu: true,
            onChange: (value) => {
                leaveConfig.detailTeamId = value || null;
                updateSaveButtonHandler(true);
            },
        });
    }

    const defaultTypeRoot = container.querySelector("[data-leave-default-type-control]");
    if (defaultTypeRoot) {
        createSelectControl({
            root: defaultTypeRoot,
            options: getActiveLeaveTypeOptions(leaveConfig),
            value: leaveConfig.defaultTypeId || "",
            accessibleName: "Default leave type",
            placeholder: "No default",
            clearable: true,
            portalMenu: true,
            onChange: (value) => {
                leaveConfig.defaultTypeId = value || null;
                updateSaveButtonHandler(true);
            },
        });
    }
}

function moveLeaveType(index, direction) {
    const leaveTypes = ensureLeaveConfig().types;
    const target = leaveTypes[index];
    if (!target || typeof target !== "object") {return false;}
    const targetIsActive = target.isActive !== false;
    const sorted = leaveTypes
        .map((type, typeIndex) => ({
            index: typeIndex,
            type,
            order: Number(type?.order ?? typeIndex + 1) || typeIndex + 1,
        }))
        .filter((entry) => (entry.type?.isActive !== false) === targetIsActive)
        .sort((a, b) => a.order - b.order);
    const currentPosition = sorted.findIndex((entry) => entry.index === index);
    const nextPosition = direction === "up"
        ? currentPosition - 1
        : currentPosition + 1;
    if (nextPosition < 0 || nextPosition >= sorted.length) {return false;}

    const current = sorted[currentPosition].type;
    const next = sorted[nextPosition].type;
    const currentOrder = Number(current.order) || sorted[currentPosition].order;
    const nextOrder = Number(next.order) || sorted[nextPosition].order;
    current.order = nextOrder;
    next.order = currentOrder;
    return true;
}

/**
 * Renders the admin Leave settings tab.
 *
 * @param {HTMLElement} container Settings tab container.
 * @param {object} options Render options.
 * @param {Function} options.updateSaveButton Dirty-state callback.
 * @returns {void}
 */
export function renderLeaveSettings(container, { updateSaveButton } = {}) {
    updateSaveButtonHandler = updateSaveButton || (() => {});
    const leaveConfig = ensureLeaveConfig();
    destroyRegisteredControlsIn(container);
    container.innerHTML = `
        <div class="space-y-6">
            <section class="bg-white rounded-lg shadow-md p-6 space-y-5">
                <div>
                    <h3 class="text-lg font-semibold text-gray-900">Leave settings</h3>
                    <p class="mt-1 text-sm text-gray-500">
                        Configure approval routing, escalation timing, and team access.
                    </p>
                </div>
                <div class="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <div class="space-y-1">
                        <label class="block text-sm font-medium text-gray-700">Approval mode</label>
                        <div data-leave-approval-mode-control></div>
                    </div>
                    <div class="space-y-1">
                        <label class="block text-sm font-medium text-gray-700">Leave management team</label>
                        <div data-leave-detail-team-control></div>
                        <p class="settings-helper-text mt-1">
                            This team can view restricted leave details, receives team leave notifications, and can manage leave for others.
                        </p>
                    </div>
                    <div class="space-y-1">
                        <label class="block text-sm font-medium text-gray-700" for="leave-escalation-timeout">
                            Escalation timeout
                        </label>
                        <div class="flex items-center gap-2">
                            <input
                                id="leave-escalation-timeout"
                                type="number"
                                min="1"
                                max="8760"
                                step="1"
                                inputmode="numeric"
                                class="${NATIVE_INPUT_CLASSES} max-w-28 text-center"
                                value="${attrValue(normalizeWholeNumber(leaveConfig.escalationTimeoutHours, 72))}"
                                data-leave-setting="escalationTimeoutHours"
                            >
                            <span class="text-sm text-gray-500">hours</span>
                        </div>
                    </div>
                </div>
            </section>
            ${renderLeaveTypesEditor(leaveConfig)}
        </div>
    `;
    bindNativeFieldBehavior(container);
    mountLeaveSelectControls(container);
    if (pendingLeaveTypeFocusId) {
        requestLeaveTypeFocus(pendingLeaveTypeFocusId);
    }
}

/**
 * Applies Leave settings input changes.
 *
 * @param {HTMLElement} target Event target.
 * @returns {boolean} True when handled.
 */
export function handleLeaveSettingsChange(
    target,
    { rerenderCurrentTabContent = () => {} } = {},
) {
    const leaveConfig = ensureLeaveConfig();
    if (target?.matches?.("[data-leave-show-archived]")) {
        showArchivedLeaveTypes = target.checked;
        rerenderCurrentTabContent();
        return true;
    }

    if (target?.dataset?.leaveSetting) {
        const key = target.dataset.leaveSetting;
        leaveConfig[key] = normalizeWholeNumber(target.value, 0);
        updateSaveButtonHandler(true);
        return true;
    }

    const typeIndex = Number.parseInt(target?.dataset?.index, 10);
    const type = Number.isInteger(typeIndex) ? leaveConfig.types[typeIndex] : null;
    if (!type) {return false;}

    if (target.matches("[data-leave-type-name]")) {
        type.name = String(target.value || "").trim();
        updateSaveButtonHandler(true);
        return true;
    }

    return false;
}

/**
 * Handles Leave settings buttons.
 *
 * @param {HTMLElement} target Event target.
 * @param {object} options Handler dependencies.
 * @param {Function} options.rerenderCurrentTabContent Rerender callback.
 * @returns {boolean} True when handled.
 */
export function handleLeaveSettingsButtonClick(
    target,
    { rerenderCurrentTabContent = () => {} } = {},
) {
    const addButton = target?.closest?.(".add-leave-type-btn");
    if (addButton) {
        const leaveTypes = ensureLeaveConfig().types;
        const id = `leave_type_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;
        leaveTypes.push({
            id,
            name: "New Leave Type",
            color: "#DCFCE7",
            order: getMaxLeaveTypeOrder() + 1,
            isActive: true,
            hasBeenUsed: false,
        });
        updateSaveButtonHandler(true);
        pendingLeaveTypeFocusId = id;
        rerenderCurrentTabContent();
        return true;
    }

    const orderButton = target?.closest?.(".leave-type-order-btn");
    if (orderButton) {
        const index = Number.parseInt(orderButton.dataset.index, 10);
        if (moveLeaveType(index, orderButton.dataset.direction)) {
            updateSaveButtonHandler(true);
            rerenderCurrentTabContent();
        }
        return true;
    }

    const deleteButton = target?.closest?.(".delete-leave-type-btn");
    if (deleteButton) {
        const index = Number.parseInt(deleteButton.dataset.index, 10);
        const type = ensureLeaveConfig().types[index];
        if (!type || type.hasBeenUsed === true) {return true;}
        openConfirmationModal(
            "Delete Leave Type?",
            "This leave type will be removed from new leave requests.",
            () => {
                if (ensureLeaveConfig().defaultTypeId === type.id) {
                    ensureLeaveConfig().defaultTypeId = null;
                }
                ensureLeaveConfig().types.splice(index, 1);
                updateSaveButtonHandler(true);
                rerenderCurrentTabContent();
            },
        );
        return true;
    }

    const archiveButton = target?.closest?.(".archive-leave-type-btn");
    if (archiveButton) {
        const index = Number.parseInt(archiveButton.dataset.index, 10);
        const type = ensureLeaveConfig().types[index];
        if (!type || type.hasBeenUsed !== true) {return true;}
        type.isActive = false;
        if (ensureLeaveConfig().defaultTypeId === type.id) {
            ensureLeaveConfig().defaultTypeId = null;
        }
        updateSaveButtonHandler(true);
        rerenderCurrentTabContent();
        return true;
    }

    const restoreButton = target?.closest?.(".restore-leave-type-btn");
    if (restoreButton) {
        const index = Number.parseInt(restoreButton.dataset.index, 10);
        const type = ensureLeaveConfig().types[index];
        if (!type) {return true;}
        type.isActive = true;
        type.order = getMaxLeaveTypeOrder() + 1;
        updateSaveButtonHandler(true);
        rerenderCurrentTabContent();
        return true;
    }

    return false;
}

/**
 * Validates the Leave settings draft before save.
 *
 * @returns {boolean} True when valid.
 */
export function validateLeaveSettingsDraft() {
    const leaveConfig = ensureLeaveConfig();
    const approvalMode = String(leaveConfig.approvalMode || "");
    if (!APPROVAL_MODE_OPTIONS.some((option) => option.value === approvalMode)) {
        openConfirmationModal(
            "Leave Settings",
            "Select a valid leave approval mode.",
            () => {},
            false,
            "OK",
        );
        return false;
    }

    const escalationHours = normalizeWholeNumber(
        leaveConfig.escalationTimeoutHours,
        -1,
    );
    if (escalationHours < 1 || escalationHours > 8760) {
        openConfirmationModal(
            "Leave Settings",
            "Escalation timeout must be a whole number from 1 to 8760 hours.",
            () => {},
            false,
            "OK",
        );
        return false;
    }

    if (leaveConfig.detailTeamId) {
        const teamExists = (appData.teams || []).some((team) =>
            String(team?.id || "") === String(leaveConfig.detailTeamId));
        if (!teamExists) {
            openConfirmationModal(
                "Leave Settings",
                "Leave management team must be an existing shared team.",
                () => {},
                false,
                "OK",
            );
            return false;
        }
    }

    const invalidType = leaveConfig.types.find((type) =>
        !String(type?.name || "").trim());
    if (invalidType) {
        openConfirmationModal(
            "Leave Settings",
            "Every leave type must have a name.",
            () => {},
            false,
            "OK",
        );
        return false;
    }

    if (leaveConfig.defaultTypeId) {
        const defaultType = leaveConfig.types.find((type) =>
            type?.id === leaveConfig.defaultTypeId);
        if (!defaultType || defaultType.isActive === false) {
            openConfirmationModal(
                "Leave Settings",
                "Default leave type must be an active leave type.",
                () => {},
                false,
                "OK",
            );
            return false;
        }
    }

    leaveConfig.escalationTimeoutHours = escalationHours;
    return true;
}
